<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  displayedBasicRows: any[];
  tempFilters: ReadonlyArray<{ value: string; label: string }>;
  selectedTemp: string;
  sortFilters: ReadonlyArray<{ value: string; label: string; hidden?: boolean }>;
  selectedSort: string;
  searchKeyword: string;
  countText: string;
  basicTempMeta: Record<string, { label: string; bg: string; border: string; color: string }>;
  formatPostCount: (n: number) => string;
}>();

const emit = defineEmits<{
  (e: 'update:searchKeyword', value: string): void;
  (e: 'update:selectedTemp', value: string): void;
  (e: 'update:selectedSort', value: string): void;
}>();

const searchKeywordModel = computed({
  get: () => props.searchKeyword,
  set: (v: string) => emit('update:searchKeyword', v),
});
</script>

<template>
  <div class="hd-panel hd-basic-panel">
    <div class="hd-panel-accent hd-panel-accent--red-orange"></div>
    <div class="hd-panel-header hd-panel-header--basic">
      <div class="hd-panel-header-text">
        <h3 class="hd-panel-title">キーワード別 基本分析</h3>
        <p class="hd-panel-subtitle">投稿数・主年齢</p>
      </div>
    </div>

    <div class="hd-toolbar hd-toolbar--basic">
      <div class="hd-search">
        <input
          v-model="searchKeywordModel"
          class="hd-search-input"
          type="text"
          placeholder="キーワードを検索..."
        />
      </div>
      <div class="hd-filter-group">
        <span class="hd-filter-label">温度:</span>
        <button
          v-for="opt in tempFilters"
          :key="opt.value"
          class="hd-chip"
          :class="{ 'is-active-dark': selectedTemp === opt.value }"
          @click="emit('update:selectedTemp', opt.value)"
        >{{ opt.label }}</button>
      </div>
      <div class="hd-toolbar-right">
        <div class="hd-filter-group">
          <span class="hd-filter-label">並び順:</span>
          <button
            v-for="opt in sortFilters"
            v-show="!opt.hidden"
            :key="opt.value"
            class="hd-chip"
            :class="{ 'is-active-orange': selectedSort === opt.value }"
            @click="emit('update:selectedSort', opt.value)"
          >{{ opt.label }}</button>
        </div>
        <span class="hd-count">{{ countText }}</span>
      </div>
    </div>

    <div class="hd-table-scroll">
      <table v-if="displayedBasicRows.length > 0" class="hd-basic-table">
        <thead>
          <tr>
            <th class="hd-b-col-keyword">キーワード</th>
            <th class="hd-b-col-post">投稿数</th>
            <th class="hd-b-col-age">主年齢</th>
            <th class="hd-b-col-fill"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in displayedBasicRows" :key="row.keyword">
            <td class="hd-b-cell-keyword">
              <span class="hd-keyword-dot" :style="{ background: row.dotColor }"></span>
              <span class="hd-keyword-text">{{ row.keyword }}</span>
              <span
                class="hd-heat-badge"
                :class="`hd-heat-badge--${row.temp}`"
              >{{ basicTempMeta[row.temp].label }}</span>
            </td>
            <td class="hd-b-cell-post">{{ formatPostCount(row.mentionCount) }}</td>
            <td class="hd-b-cell-age">{{ row.mainAgeGroup }}</td>
            <td class="hd-b-cell-fill"></td>
          </tr>
        </tbody>
      </table>
      <div v-else class="hd-panel-empty">データがありません</div>
    </div>
  </div>
</template>

<style scoped lang="less">
.hd-panel { border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-panel); overflow: hidden; color: var(--text-primary); }
.hd-panel-accent { height: 3px; width: 100%; }
.hd-panel-accent--red-orange { background: linear-gradient(90deg, var(--accent-red) 0%, var(--accent-orange) 100%); }
.hd-panel-header { padding: 12px 16px; border-bottom: 1px solid var(--border-color); }
.hd-panel-title { margin: 0; font-size: 12px; font-weight: 700; color: var(--text-primary); }
.hd-panel-subtitle { margin: 2px 0 0; font-size: 10px; color: var(--text-tertiary); }
.hd-toolbar { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; padding: 12px 16px; border-bottom: 1px solid var(--border-color); }
.hd-search { width: 200px; }
.hd-search-input {
  width: 100%;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  background: transparent;
  color: var(--text-primary);
}
.hd-search-input::placeholder { color: var(--text-tertiary); }
.hd-filter-group { display: flex; align-items: center; gap: 6px; }
.hd-filter-label { font-size: 11px; color: var(--text-secondary); }
.hd-chip {
  border: 1px solid transparent;
  background: color-mix(in srgb, var(--text-primary) 6%, var(--bg-panel));
  color: var(--text-secondary);
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--text-primary);
    background: color-mix(in srgb, var(--text-primary) 10%, var(--bg-panel));
  }
}
/* ライト: 黒塗り反転。ダーク: ブランド黄ハイライト（白ベタを避ける） */
.hd-chip.is-active-dark {
  background: var(--text-primary);
  color: var(--bg-body);
  font-weight: 600;
}
.hd-chip.is-active-orange {
  background: var(--accent-orange);
  color: #fff;
  font-weight: 600;
}

html.dark .hd-chip {
  background: color-mix(in srgb, #ffffff 6%, var(--bg-panel));
  color: var(--text-secondary);

  &:hover {
    background: color-mix(in srgb, #ffffff 10%, var(--bg-panel));
    color: var(--text-primary);
  }
}

html.dark .hd-chip.is-active-dark {
  background: color-mix(in srgb, var(--brand-color, #f9da49) 22%, var(--bg-panel));
  color: var(--text-primary);
  border-color: color-mix(in srgb, var(--brand-color, #f9da49) 45%, transparent);
}

html.dark .hd-chip.is-active-orange {
  background: color-mix(in srgb, var(--accent-orange, #fb923c) 28%, var(--bg-panel));
  color: var(--accent-orange, #fb923c);
  border-color: color-mix(in srgb, var(--accent-orange, #fb923c) 50%, transparent);
}
.hd-toolbar-right { margin-left: auto; display: flex; gap: 12px; align-items: center; }
.hd-count { font-size: 11px; color: var(--text-tertiary); }
.hd-table-scroll { width: 100%; overflow-x: auto; }
.hd-panel-empty { display: flex; align-items: center; justify-content: center; min-height: 120px; color: var(--text-tertiary); font-size: 13px; }
.hd-basic-table { width: 100%; border-collapse: collapse; table-layout: fixed; min-width: 360px; }
.hd-basic-table th, .hd-basic-table td { border-bottom: 1px solid var(--border-color); height: 36px; padding: 0 12px; }
.hd-basic-table thead th { background: var(--bg-body); font-size: 10px; color: var(--text-secondary); text-align: left; }
.hd-b-col-keyword { width: 220px; }
.hd-b-col-post { width: 88px; }
.hd-b-col-age { width: 72px; }
.hd-b-cell-keyword { white-space: nowrap; }
.hd-keyword-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 6px; }
.hd-keyword-text { font-size: 11px; font-weight: 600; margin-right: 6px; color: var(--text-primary); }
.hd-heat-badge { font-size: 9px; border: 1px solid; border-radius: 3px; padding: 2px 6px; }
.hd-heat-badge--ultra { background: #FEF2F2; border-color: #FCA5A5; color: #DC2626; }
.hd-heat-badge--hot { background: #FDF2F8; border-color: #F9A8D4; color: #EC4899; }
.hd-heat-badge--high { background: #FFF7ED; border-color: #FDBA74; color: #EA580C; }
.hd-heat-badge--mid { background: #F0FDF4; border-color: #86EFAC; color: #16A34A; }
.hd-heat-badge--low { background: #F8FAFC; border-color: #CBD5E1; color: #6B7280; }
.hd-b-cell-post { font-size: 11px; font-weight: 600; color: var(--text-primary); }
.hd-b-cell-age { font-size: 11px; font-weight: 600; color: var(--accent-blue); }

html.dark .hd-heat-badge--ultra { background: rgba(220, 38, 38, 0.18); border-color: rgba(252, 165, 165, 0.45); color: #f87171; }
html.dark .hd-heat-badge--hot { background: rgba(236, 72, 153, 0.18); border-color: rgba(249, 168, 212, 0.45); color: var(--accent-pink); }
html.dark .hd-heat-badge--high { background: rgba(234, 88, 12, 0.18); border-color: rgba(253, 186, 116, 0.45); color: var(--accent-orange); }
html.dark .hd-heat-badge--mid { background: rgba(22, 163, 74, 0.18); border-color: rgba(134, 239, 172, 0.45); color: var(--accent-green); }
html.dark .hd-heat-badge--low { background: rgba(148, 163, 184, 0.16); border-color: rgba(203, 213, 225, 0.35); color: var(--text-secondary); }
</style>
